/**
 * Public platform blog (no auth).
 */
import { get, post, getBaseUrl } from "../utils/api";

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

export async function listBlogCategories(lang) {
  const q = new URLSearchParams();
  if (lang) q.set("lang", lang);
  const res = await get(`/public/blog/categories?${q}`);
  return asArray(res.data);
}

export async function listBlogTags(lang) {
  const q = new URLSearchParams();
  if (lang) q.set("lang", lang);
  const res = await get(`/public/blog/tags?${q}`);
  return asArray(res.data);
}

export async function listBlogPosts({ lang, category, tag, page = 1, limit = 12 }) {
  const q = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (lang) q.set("lang", lang);
  if (category) q.set("category", category);
  if (tag) q.set("tag", tag);
  const res = await get(`/public/blog/posts?${q}`);
  return { posts: asArray(res.data), meta: res.meta || {} };
}

export async function getBlogPost(categorySlug, postSlug, lang) {
  const q = new URLSearchParams();
  if (lang) q.set("lang", lang);
  const encC = encodeURIComponent(categorySlug);
  const encP = encodeURIComponent(postSlug);
  const res = await get(`/public/blog/posts/${encC}/${encP}?${q}`);
  return res.data || null;
}

export function blogRssUrl(lang) {
  try {
    const base = getBaseUrl();
    if (!base) return "";
    return `${base}/public/blog/rss?lang=${encodeURIComponent(lang || "en")}`;
  } catch {
    return "";
  }
}

export async function searchBlogPosts({ lang, q, page = 1, limit = 10 }) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (lang) params.set("lang", lang);
  if (q) params.set("q", q);
  const res = await get(`/public/blog/search?${params}`);
  return { posts: asArray(res.data), meta: res.meta || {} };
}

export async function listBlogAuthors(lang) {
  const q = new URLSearchParams();
  if (lang) q.set("lang", lang);
  const res = await get(`/public/blog/authors?${q}`);
  return asArray(res.data);
}

export async function getBlogAuthor(slug, lang) {
  const q = new URLSearchParams();
  if (lang) q.set("lang", lang);
  const res = await get(`/public/blog/authors/${encodeURIComponent(slug)}?${q}`);
  return res.data || null;
}

// ─── Analytics tracking ─────────────────────────────────────────────────────

let _visitorId = null;
function getVisitorId() {
  if (_visitorId) return _visitorId;
  try {
    _visitorId = localStorage.getItem("pb_vid");
    if (!_visitorId) {
      _visitorId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("pb_vid", _visitorId);
    }
  } catch { _visitorId = "anon-" + Math.random().toString(36).slice(2); }
  return _visitorId;
}

let _sessionId = null;
function getSessionId() {
  if (_sessionId) return _sessionId;
  try {
    _sessionId = sessionStorage.getItem("pb_sid");
    if (!_sessionId) {
      _sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("pb_sid", _sessionId);
    }
  } catch { _sessionId = "s-" + Math.random().toString(36).slice(2); }
  return _sessionId;
}

export async function trackPageView({ post_id, page_type, page_path, locale }) {
  try {
    const params = new URLSearchParams(window.location.search);
    const isReturning = !!localStorage.getItem("pb_visited");
    localStorage.setItem("pb_visited", "1");

    // Collect rich client data
    const screen_width = window.screen?.width || null;
    const screen_height = window.screen?.height || null;
    const viewport_width = window.innerWidth || null;
    const viewport_height = window.innerHeight || null;
    const language = navigator.language || null;
    const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || null;
    let connection_type = null;
    try { connection_type = navigator.connection?.effectiveType || null; } catch {}

    const res = await post("/public/blog/track/view", {
      post_id, page_type: page_type || "post", page_path: page_path || window.location.pathname,
      visitor_id: getVisitorId(), session_id: getSessionId(), locale,
      utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"), utm_campaign: params.get("utm_campaign"),
      referer: document.referrer || null,
      screen_width, screen_height, viewport_width, viewport_height,
      language, timezone, connection_type,
      is_returning: isReturning,
    });
    return res?.pv_id || null;
  } catch { return null; }
}

export function trackEngagement(pvId, { time_on_page_seconds, scroll_depth_percent, read_complete }) {
  if (!pvId) return;
  const data = JSON.stringify({ pv_id: pvId, time_on_page_seconds, scroll_depth_percent, read_complete });
  try {
    const base = getBaseUrl();
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${base}/public/blog/track/engagement`, new Blob([data], { type: "application/json" }));
    } else {
      fetch(`${base}/public/blog/track/engagement`, { method: "POST", body: data, headers: { "Content-Type": "application/json" }, keepalive: true });
    }
  } catch { /* silent */ }
}

export async function trackEvent({ pv_id, post_id, event_type, event_target, event_value }) {
  try {
    await post("/public/blog/track/event", {
      pv_id, post_id, visitor_id: getVisitorId(), event_type, event_target, event_value,
    });
  } catch { /* silent */ }
}

// ─── Business analytics tracking ────────────────────────────────────────────

export async function trackBusinessView({ business_id, page_type, locale }) {
  try {
    const params = new URLSearchParams(window.location.search);
    const res = await post("/public/business-analytics/track/view", {
      business_id, page_type: page_type || "profile",
      visitor_id: getVisitorId(), session_id: getSessionId(), locale,
      utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"), utm_campaign: params.get("utm_campaign"),
      referer: document.referrer || null,
      screen_width: window.screen?.width, viewport_width: window.innerWidth,
      language: navigator.language, timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone,
      is_returning: !!localStorage.getItem("pb_visited"),
    });
    return res?.pv_id || null;
  } catch { return null; }
}

export async function trackBusinessEvent({ business_id, pv_id, event_type, event_target }) {
  try {
    await post("/public/business-analytics/track/event", {
      business_id, pv_id, visitor_id: getVisitorId(), event_type, event_target,
    });
  } catch { /* silent */ }
}

export function trackBusinessEngagement(pvId, { time_on_page_seconds, scroll_depth_percent }) {
  if (!pvId) return;
  const data = JSON.stringify({ pv_id: pvId, time_on_page_seconds, scroll_depth_percent });
  try {
    const base = getBaseUrl();
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${base}/public/business-analytics/track/engagement`, new Blob([data], { type: "application/json" }));
    }
  } catch { /* silent */ }
}

/** XML sitemap for all locales + posts (submit in Search Console). */
export function blogSitemapUrl() {
  try {
    const base = getBaseUrl();
    if (!base) return "";
    return `${base}/public/blog/sitemap.xml`;
  } catch {
    return "";
  }
}
